/**
 * Shared middleware auth timeout / fail-open primitives.
 *
 * Extracted for path-to-go S26 so web and client-portal middleware share ONE
 * bounded-auth implementation (commit bb45fd8c pattern). App-specific routing,
 * CSP, and role redirects stay in each app's updateSession; the multi-surface
 * defect was divergent timeout/fail-open behaviour on portal.
 *
 * Consumed via `@statura/utils/middleware-auth-timeout`.
 */

import { isSupabaseAuthCookieName } from './supabase-auth-cookies';

export const AUTH_GETUSER_TIMEOUT_MS = 2500;
export const PROFILE_LOOKUP_TIMEOUT_MS = 1500;
export const MIDDLEWARE_AUTH_TIMED_OUT = Symbol('middleware-auth-timed-out');

export type MiddlewareAuthCookie = { name: string; value: string };

/**
 * Race a promise against a client-side timeout. On timeout, returns the
 * MIDDLEWARE_AUTH_TIMED_OUT sentinel instead of hanging to the platform
 * middleware limit (MIDDLEWARE_INVOCATION_TIMEOUT 504 class).
 *
 * Late rejections of the losing promise are swallowed so they never surface
 * as unhandled rejections after the timeout already won.
 */
export async function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
): Promise<T | typeof MIDDLEWARE_AUTH_TIMED_OUT> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<typeof MIDDLEWARE_AUTH_TIMED_OUT>((resolve) => {
    timer = setTimeout(() => resolve(MIDDLEWARE_AUTH_TIMED_OUT), ms);
  });
  void promise.catch(() => {});
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** True when the request carries any Supabase auth-token cookie (chunked or not). */
export function requestHasSupabaseSessionCookie(cookies: readonly MiddlewareAuthCookie[]): boolean {
  return cookies.some((c) => isSupabaseAuthCookieName(c.name));
}

export function isSoftNullAuthFailure(error: Error | null | undefined): boolean {
  return error?.message.includes('getAuthUserWithRetry: getUser() returned a null user') === true;
}

/**
 * Transient Auth REST failures that should fail-open when a session cookie is
 * already present (middleware is defence-in-depth; pages/actions re-validate).
 */
export function isTransientAuthFailure(error: Error | null | undefined): boolean {
  if (!error) return false;
  if (isSoftNullAuthFailure(error)) return true;
  if (error.name === 'AuthRetryableFetchError') return true;
  const message = error.message.toLowerCase();
  return (
    message.includes('load failed') ||
    message.includes('failed to fetch') ||
    message.includes('fetch failed') ||
    message.includes('network') ||
    message.includes('timeout') ||
    message.includes('econnreset') ||
    message.includes('etimedout') ||
    message.includes('rate limit') ||
    /(?:^|\D)5(?:00|01|02|03|04|20|21|22|23|24)(?:\D|$)/.test(message)
  );
}

/**
 * Retry wrapper for `supabase.auth.getUser()`.
 *
 * Shared across web + portal middleware so both surfaces get the same soft-null
 * and transient-failure tolerance. Still calls getUser() (JWT validation),
 * never getSession().
 *
 * Duck-typed client so packages/utils does not need a hard @supabase dependency.
 */
export type MiddlewareAuthGetUserClient = {
  auth: {
    getUser: () => PromiseLike<{
      data?: { user?: unknown | null } | null;
      error?: {
        message?: string;
        name?: string;
        status?: number;
        code?: string;
      } | null;
    }>;
  };
};

export type MiddlewareAuthError = Error & {
  status?: number;
  code?: string;
};

function preserveAuthError(
  error: NonNullable<Awaited<ReturnType<MiddlewareAuthGetUserClient['auth']['getUser']>>['error']>,
): MiddlewareAuthError {
  if (error instanceof Error) return error as MiddlewareAuthError;

  const preserved = new Error(error.message || 'auth.getUser failed') as MiddlewareAuthError;
  if (error.name) preserved.name = error.name;
  if (typeof error.status === 'number') preserved.status = error.status;
  if (typeof error.code === 'string') preserved.code = error.code;
  return preserved;
}

export async function getAuthUserWithRetry(
  supabase: MiddlewareAuthGetUserClient,
  opts: { retries?: number; delayMs?: number } = {},
): Promise<{ data: { user: unknown | null }; error: MiddlewareAuthError | null }> {
  const retries = opts.retries ?? 1;
  const delayMs = opts.delayMs ?? 100;
  let lastError: MiddlewareAuthError | null = null;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const result = await supabase.auth.getUser();
      if (result.data?.user) {
        return { data: { user: result.data.user }, error: null };
      }
      if (result.error) {
        lastError = preserveAuthError(result.error);
      }
    } catch (err) {
      lastError = err instanceof Error ? (err as MiddlewareAuthError) : new Error(String(err));
    }
    if (attempt < retries) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
  return {
    data: { user: null },
    error:
      lastError ??
      new Error(
        'getAuthUserWithRetry: getUser() returned a null user with no error on all attempts',
      ),
  };
}
